import {type PartialZoom, type UserZoomIntentPayload} from '../types';
import {
  type Boundary,
  type ExternalBounded,
  type ExternalMax,
  type ExternalMin,
  type FullyBounded,
  type InternalBounded,
  type PartialMaybeDomain,
} from './types';
import {getZoomIntent} from './zoomIntent';

export function isMetricKeyAbsoluteRuntime(metricKey: string) {
  return metricKey === '_absolute_runtime';
}

export function makeSafeZoom(z: Record<string, number | null | undefined>) {
  return Object.entries(z).reduce(
    (acc, [key, val]) => {
      if (typeof val === 'number' && Number.isFinite(val)) {
        acc[key] = val;
      } else {
        acc[key] = null;
      }
      return acc;
    },
    {} as Record<string, number | null>
  );
}

export function isInRange(
  point: number | string,
  // two null domains have been filtered out above
  [min, max]: PartialMaybeDomain
) {
  if (typeof point !== 'number') {
    // NaN, Infinity, -Infinity come in as strings
    return false;
  }
  if (min == null && max !== null) {
    return point <= max;
  }
  if (max == null) {
    return point >= min;
  }
  return point >= min && point <= max;
}

export function hasLogicalMaxBoundary(b: ExternalMax) {
  return b.externalMax > b.internalMax;
}

export function hasLogicalMinBoundary(b: ExternalMin) {
  return b.externalMin < b.internalMin;
}

export function hasMinBoundary(b: InternalBounded): b is ExternalMin {
  return Number.isFinite(b.externalMin);
}

export function hasMaxBoundary(b: InternalBounded): b is ExternalMax {
  return Number.isFinite(b.externalMax);
}

export function hasLogicalBoundaries(b: FullyBounded) {
  return hasLogicalMinBoundary(b) && hasLogicalMaxBoundary(b);
}

export function hasInternalBounds(b: Boundary): b is InternalBounded {
  return Number.isFinite(b.internalMax) && Number.isFinite(b.internalMin);
}

export function isMissingExternalBounds(b: Boundary) {
  return b.externalMin === null && b.externalMax === null;
}

export function hasExternalBounds(b: InternalBounded): b is FullyBounded;
export function hasExternalBounds(b: Boundary): b is ExternalBounded;
export function hasExternalBounds(b: Boundary): boolean {
  return Number.isFinite(b.externalMax) && Number.isFinite(b.externalMin);
}

/**
 * Returns true when a single axis's user-zoom `[min, max]` range lies entirely
 * outside the config-zoom `[configMin, configMax]` range — i.e. the ranges do
 * not overlap on this axis.
 *
 * Used to enforce PRODUCT_RULES §5.4 / §6.5 ("Config Zoom cap → exclusion") at
 * the dispatch boundary so a gesture entirely outside Config Zoom never
 * advances `state.userZoom`. Without this guard, only `queryZoom` is clipped at
 * derivation, leaving `state.userZoom` and the derived `isZooming` flag
 * advanced even though the user's gesture had no observable effect.
 *
 * Returns false when:
 * - both config bounds are absent (no cap on this axis)
 * - both user bounds are absent (no gesture on this axis)
 * - the user range overlaps config in any way (clipped at derivation, not excluded)
 */
export function isAxisEntirelyOutsideConfigRange(
  userMin: number | null | undefined,
  userMax: number | null | undefined,
  configMin: number | null | undefined,
  configMax: number | null | undefined
): boolean {
  if (configMin == null && configMax == null) {
    return false;
  }
  if (userMin == null && userMax == null) {
    return false;
  }
  if (configMax != null && userMin != null && userMin > configMax) {
    return true;
  }
  if (configMin != null && userMax != null && userMax < configMin) {
    return true;
  }
  return false;
}

/**
 * PRODUCT_RULES §5.4: a gesture is excluded when its bounds lie entirely
 * outside Config Zoom on at least one axis it targets. Reset gestures (intent
 * false/false) are never excluded — both `xExcluded` and `yExcluded` are
 * `false` via `&&` short-circuit on the intent flags.
 */
export function isGestureExcludedByConfigZoom(
  zoom: UserZoomIntentPayload,
  configZoom: PartialZoom
): boolean {
  const intent = getZoomIntent(zoom);
  const xExcluded =
    intent.shouldZoomX &&
    isAxisEntirelyOutsideConfigRange(
      zoom.xAxisMin,
      zoom.xAxisMax,
      configZoom.xAxisMin,
      configZoom.xAxisMax
    );
  const yExcluded =
    intent.shouldZoomY &&
    isAxisEntirelyOutsideConfigRange(
      zoom.yAxisMin,
      zoom.yAxisMax,
      configZoom.yAxisMin,
      configZoom.yAxisMax
    );
  return xExcluded || yExcluded;
}

export function getConfigBoundedZoom(
  configMin: number | null,
  configMax: number | null,
  queryMin: number | null,
  queryMax: number | null
) {
  if (configMin == null && configMax == null) {
    return {
      min: queryMin ?? null,
      max: queryMax ?? null,
    };
  }

  // if the minimum query is out of bounds return the config values
  if (configMax != null && queryMin != null && queryMin > configMax) {
    return {
      min: configMin ?? null,
      max: configMax ?? null,
    };
  }

  // if the maximum query is out of bounds return the config
  if (configMin != null && queryMax != null && queryMax < configMin) {
    return {
      min: configMin ?? null,
      max: configMax ?? null,
    };
  }

  const min = Math.max(queryMin ?? -Infinity, configMin ?? -Infinity);
  const max = Math.min(queryMax ?? Infinity, configMax ?? Infinity);

  return {
    min: min === -Infinity ? null : min,
    max: max === Infinity ? null : max,
  };
}
